// Serviço de Segurança - Sistema de Agendamento UP V.4.0
// Implementação de rate limiting e validações de segurança

interface LoginAttempt {
  timestamp: number;
  success: boolean;
  ip?: string;
  userAgent?: string;
}

interface SecurityConfig {
  maxAttempts: number;
  lockoutTime: number;
  sessionTimeout: number;
  passwordMinLength: number;
  passwordRequireSpecial: boolean;
}

class SecurityService {
  private static instance: SecurityService;
  private loginAttempts: Map<string, LoginAttempt[]> = new Map();
  private lockedAccounts: Map<string, number> = new Map();
  private config: SecurityConfig;

  constructor() {
    this.config = {
      maxAttempts: parseInt(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS || '5'),
      lockoutTime: parseInt(process.env.REACT_APP_LOGIN_LOCKOUT_TIME || '900000'), // 15 minutos
      sessionTimeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '3600000'), // 1 hora
      passwordMinLength: 8,
      passwordRequireSpecial: false
    };

    // Limpar tentativas antigas periodicamente
    setInterval(() => this.cleanupOldAttempts(), 60000); // A cada minuto
  }

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  // Rate limiting para login
  canAttemptLogin(identifier: string): { allowed: boolean; reason?: string; timeRemaining?: number } {
    const now = Date.now();
    
    // Verificar se conta está bloqueada
    const lockoutEnd = this.lockedAccounts.get(identifier);
    if (lockoutEnd && lockoutEnd > now) {
      const timeRemaining = Math.ceil((lockoutEnd - now) / 1000 / 60); // minutos
      return {
        allowed: false,
        reason: `Conta bloqueada por excesso de tentativas. Tente novamente em ${timeRemaining} minutos.`,
        timeRemaining: lockoutEnd - now
      };
    }

    // Verificar tentativas recentes
    const attempts = this.loginAttempts.get(identifier) || [];
    const recentAttempts = attempts.filter(attempt => 
      now - attempt.timestamp < this.config.lockoutTime
    );

    const failedAttempts = recentAttempts.filter(attempt => !attempt.success).length;
    
    if (failedAttempts >= this.config.maxAttempts) {
      // Bloquear conta
      const lockoutEnd = now + this.config.lockoutTime;
      this.lockedAccounts.set(identifier, lockoutEnd);
      
      // Limpar tentativas antigas
      this.loginAttempts.set(identifier, recentAttempts);
      
      const timeRemaining = Math.ceil(this.config.lockoutTime / 1000 / 60);
      return {
        allowed: false,
        reason: `Conta bloqueada por excesso de tentativas. Tente novamente em ${timeRemaining} minutos.`,
        timeRemaining: this.config.lockoutTime
      };
    }

    return { allowed: true };
  }

  // Registrar tentativa de login
  recordLoginAttempt(identifier: string, success: boolean, ip?: string, userAgent?: string): void {
    const now = Date.now();
    const attempts = this.loginAttempts.get(identifier) || [];
    
    attempts.push({
      timestamp: now,
      success,
      ip,
      userAgent
    });

    // Manter apenas tentativas recentes
    const recentAttempts = attempts.filter(attempt => 
      now - attempt.timestamp < this.config.lockoutTime
    );

    this.loginAttempts.set(identifier, recentAttempts);

    // Se sucesso, limpar bloqueio e tentativas
    if (success) {
      this.lockedAccounts.delete(identifier);
      this.loginAttempts.delete(identifier);
    }
  }

  // Validação de senha
  validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < this.config.passwordMinLength) {
      errors.push(`Senha deve ter pelo menos ${this.config.passwordMinLength} caracteres`);
    }

    if (this.config.passwordRequireSpecial) {
      if (!/[A-Z]/.test(password)) {
        errors.push('Senha deve conter pelo menos uma letra maiúscula');
      }
      if (!/[a-z]/.test(password)) {
        errors.push('Senha deve conter pelo menos uma letra minúscula');
      }
      if (!/[0-9]/.test(password)) {
        errors.push('Senha deve conter pelo menos um número');
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push('Senha deve conter pelo menos um caractere especial');
      }
    }
    
    // Modo desenvolvimento: aceitar qualquer senha com mínimo de caracteres
    // Em produção, ativar passwordRequireSpecial

    // Verificar senhas comuns
    const commonPasswords = ['123456', 'password', '123456789', '12345678', '12345', '1234567', '1234567890', 'qwerty', 'abc123'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('Senha muito comum. Escolha uma senha mais segura');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Validação de email
  validateEmail(email: string): { valid: boolean; error?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Email inválido' };
    }

    // Verificar domínios suspeitos
    const suspiciousDomains = ['tempmail.com', '10minutemail.com', 'guerrillamail.com'];
    const domain = email.split('@')[1].toLowerCase();
    
    if (suspiciousDomains.some(suspicious => domain.includes(suspicious))) {
      return { valid: false, error: 'Domínio de email não permitido' };
    }

    return { valid: true };
  }

  // Validação de entrada (XSS protection)
  sanitizeInput(input: string): string {
    if (!input) return '';
    
    return input
      .replace(/[<>]/g, '') // Remover tags HTML
      .replace(/javascript:/gi, '') // Remover javascript
      .replace(/on\w+=/gi, '') // Remover event handlers
      .trim();
  }

  // Verificar timeout de sessão
  isSessionExpired(lastActivity: number): boolean {
    return Date.now() - lastActivity > this.config.sessionTimeout;
  }

  // Gerar token CSRF simples
  generateCSRFToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Limpar tentativas antigas
  private cleanupOldAttempts(): void {
    const now = Date.now();
    const cutoff = now - this.config.lockoutTime;

    // Converter para array para compatibilidade com TypeScript
    Array.from(this.loginAttempts.entries()).forEach(([identifier, attempts]) => {
      const recentAttempts = attempts.filter((attempt: LoginAttempt) => attempt.timestamp > cutoff);
      
      if (recentAttempts.length === 0) {
        this.loginAttempts.delete(identifier);
      } else {
        this.loginAttempts.set(identifier, recentAttempts);
      }
    });

    // Limpar contas bloqueadas expiradas
    Array.from(this.lockedAccounts.entries()).forEach(([identifier, lockoutEnd]) => {
      if (lockoutEnd <= now) {
        this.lockedAccounts.delete(identifier);
      }
    });
  }

  // Obter estatísticas de segurança
  getSecurityStats(): {
    totalAttempts: number;
    failedAttempts: number;
    lockedAccounts: number;
    mostActiveIPs: Array<{ ip: string; count: number }>;
  } {
    let totalAttempts = 0;
    let failedAttempts = 0;
    const ipCounts: Map<string, number> = new Map();

    // Converter para array para compatibilidade com TypeScript
    Array.from(this.loginAttempts.values()).forEach(attempts => {
      attempts.forEach((attempt: LoginAttempt) => {
        totalAttempts++;
        if (!attempt.success) {
          failedAttempts++;
        }
        
        if (attempt.ip) {
          ipCounts.set(attempt.ip, (ipCounts.get(attempt.ip) || 0) + 1);
        }
      });
    });

    const mostActiveIPs = Array.from(ipCounts.entries())
      .map(([ip, count]) => ({ ip, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalAttempts,
      failedAttempts,
      lockedAccounts: this.lockedAccounts.size,
      mostActiveIPs
    };
  }

  // Resetar segurança para um usuário
  resetUserSecurity(identifier: string): void {
    this.loginAttempts.delete(identifier);
    this.lockedAccounts.delete(identifier);
  }
}

// Exportar instância única
export const securityService = SecurityService.getInstance();

// Exportar classe para testes
export { SecurityService };
